import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, FormControl, ListGroup, Offcanvas, Spinner } from 'react-bootstrap';
import {
  approveChannelIdentity,
  blockChannelIdentity,
  ChannelConversation,
  ChannelIdentity,
  ChannelMessage,
  listChannelConversations,
  listChannelIdentities,
  listChannelMessages,
  rejectChannelIdentity,
  unblockChannelIdentity,
} from '../../api/channel';

interface ChannelInboxProps {
  organizationId: string;
  addToast: (message: string) => void;
  openConversationId?: string | null;
}

const ChannelInbox: React.FC<ChannelInboxProps> = ({
  organizationId,
  addToast,
  openConversationId,
}) => {
  const [conversations, setConversations] = useState<ChannelConversation[]>([]);
  const [identities, setIdentities] = useState<ChannelIdentity[]>([]);
  const [blockedIdentities, setBlockedIdentities] = useState<ChannelIdentity[]>([]);
  const [pending, setPending] = useState(false);
  const [selected, setSelected] = useState<ChannelConversation | null>(null);
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [draftCodes, setDraftCodes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setPending(true);
    try {
      const [conv, ident, blocked] = await Promise.all([
        listChannelConversations(organizationId),
        listChannelIdentities(organizationId, 'pending_access'),
        listChannelIdentities(organizationId, 'blocked'),
      ]);
      setConversations(conv.conversations || []);
      setIdentities(ident.identities || []);
      setBlockedIdentities(blocked.identities || []);
    } catch (e: any) {
      addToast(e?.message || 'Error al cargar conversaciones');
    } finally {
      setPending(false);
    }
  }, [organizationId, addToast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!openConversationId) return;
    const found = conversations.find(c => c.id === openConversationId);
    if (found) setSelected(found);
  }, [openConversationId, conversations]);

  useEffect(() => {
    if (!selected?.id) {
      setMessages([]);
      return;
    }
    void listChannelMessages(organizationId, selected.id)
      .then(r => setMessages(r.messages || []))
      .catch(() => setMessages([]));
  }, [selected?.id, organizationId]);

  return (
    <>
      {identities.length > 0 ? (
        <div className="white-card mb-4">
          <h5 className="fw-bold">Acceso pendiente</h5>
          <p className="text-secondary small mb-2">
            Aprueba para crear o activar la ficha de cliente. El código es opcional;
            si lo dejas vacío se genera automáticamente. Rechaza para que pueda
            intentarlo de nuevo. Usa bloquear solo ante abuso.
          </p>
          {identities.map(ident => (
            <div
              key={ident.id}
              className="d-flex flex-wrap gap-2 align-items-center py-2 border-bottom"
            >
              <div className="flex-grow-1">
                <div className="fw-medium">
                  {ident.displayName || ident.telegramUsername || ident.id}
                </div>
                <div className="text-secondary small">
                  {ident.pendingNote
                    || [
                      ident.pendingRif,
                      ident.pendingBusinessName,
                      ...Object.values(ident.pendingExtras || {}),
                    ]
                      .filter(Boolean)
                      .join(' · ')
                    || 'Solicitud de acceso'}
                </div>
              </div>
              <FormControl
                size="sm"
                style={{ maxWidth: 220 }}
                placeholder="Código (opcional)"
                value={draftCodes[ident.id] || ''}
                disabled={busyId === ident.id}
                onChange={e =>
                  setDraftCodes(prev => ({
                    ...prev,
                    [ident.id]: e.currentTarget.value,
                  }))
                }
              />
              <Button
                size="sm"
                disabled={busyId === ident.id}
                onClick={async () => {
                  setBusyId(ident.id);
                  try {
                    await approveChannelIdentity(
                      organizationId,
                      ident.id,
                      draftCodes[ident.id]
                    );
                    addToast('Cliente aprobado');
                    setDraftCodes(prev => {
                      const next = { ...prev };
                      delete next[ident.id];
                      return next;
                    });
                    void reload();
                  } catch (e: any) {
                    addToast(e?.message || 'No se pudo aprobar');
                  } finally {
                    setBusyId(null);
                  }
                }}
              >
                Aprobar
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={busyId === ident.id}
                onClick={async () => {
                  try {
                    await rejectChannelIdentity(organizationId, ident.id);
                    addToast('Solicitud rechazada (el usuario puede intentarlo de nuevo)');
                    void reload();
                  } catch (e: any) {
                    addToast(e?.message || 'No se pudo rechazar');
                  }
                }}
              >
                Rechazar
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                disabled={busyId === ident.id}
                onClick={async () => {
                  await blockChannelIdentity(organizationId, ident.id);
                  addToast('Usuario bloqueado');
                  void reload();
                }}
              >
                Bloquear
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      {blockedIdentities.length > 0 ? (
        <div className="white-card mb-4">
          <h5 className="fw-bold">Usuarios bloqueados</h5>
          {blockedIdentities.map(ident => (
            <div
              key={ident.id}
              className="d-flex flex-wrap gap-2 align-items-center py-2 border-bottom"
            >
              <div className="flex-grow-1">
                <div className="fw-medium">
                  {ident.displayName || ident.telegramUsername || ident.id}
                </div>
                <div className="text-secondary small">Acceso restringido</div>
              </div>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={async () => {
                  try {
                    await unblockChannelIdentity(organizationId, ident.id);
                    addToast('Usuario desbloqueado');
                    void reload();
                  } catch (e: any) {
                    addToast(e?.message || 'No se pudo desbloquear');
                  }
                }}
              >
                Desbloquear
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="white-card mb-4">
        <h5 className="fw-bold mb-3">Conversaciones</h5>
        {pending ? <Spinner animation="border" size="sm" /> : null}
        <ListGroup variant="flush">
          {conversations.map(conv => (
            <ListGroup.Item
              key={conv.id}
              action
              onClick={() => setSelected(conv)}
              className="d-flex justify-content-between align-items-start"
            >
              <div>
                <div className="fw-medium">{conv.clientName || conv.identityId}</div>
                <div className="text-secondary small">{conv.lastPreview || '—'}</div>
              </div>
              <Badge bg="light" text="dark">
                {conv.status}
              </Badge>
            </ListGroup.Item>
          ))}
          {!pending && conversations.length === 0 ? (
            <p className="text-secondary mb-0">Aún no hay conversaciones.</p>
          ) : null}
        </ListGroup>
      </div>

      <Offcanvas
        show={Boolean(selected)}
        onHide={() => setSelected(null)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {selected?.clientName || selected?.identityId}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {messages.map(m => (
            <div key={m.id} className="mb-3">
              <div className="small text-secondary">
                {m.role === 'user' ? 'Cliente' : 'Bot'}
              </div>
              <div>{m.content}</div>
            </div>
          ))}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default ChannelInbox;
